import { cn } from '@/utils/cn';
import { ChangeEvent, forwardRef, TextareaHTMLAttributes, useEffect, useRef } from 'react';

interface AutoExpandTextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows' | 'onChange'> {
  value: string;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
  minRows?: number;
  minHeight?: number;
}

// Reusable Auto-expanding Textarea Component
const AutoExpandTextarea = forwardRef<HTMLTextAreaElement, AutoExpandTextareaProps>(
  ({ value, onChange, placeholder = 'Enter text...', className = '', minRows = 1, minHeight = 0, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Merge refs: internal ref for height adjustment and forwarded ref for parent access
    const setRefs = (el: HTMLTextAreaElement | null) => {
      textareaRef.current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref && typeof ref === 'object' && 'current' in ref) {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
      }
    };

    const adjustHeight = (): void => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = 'auto';

        if (minHeight) {
          textarea.style.height = `${Math.max(minHeight, textarea.scrollHeight)}px`;
        } else {
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
      }
    };

    useEffect(() => {
      adjustHeight();
    }, [value]);

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
      onChange(e);
    };

    return (
      <textarea
        ref={setRefs}
        value={value}
        onChange={handleChange}
        rows={minRows}
        placeholder={placeholder}
        className={cn(
          'w-full px-0 py-2 border-0 border-b-2 border-slate-300 focus:border-[#0C8DBF] outline-none transition-colors resize-none font-sans text-lg text-[#25151E] bg-transparent overflow-hidden ',
          className
        )}
        {...props}
      />
    );
  }
);

AutoExpandTextarea.displayName = 'AutoExpandTextarea';
export default AutoExpandTextarea;
